import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Save, Bell, AlertCircle, Calendar, Plus, Trash2, X } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPromotion = () => {
  const navigate = useNavigate();
  const [promotion, setPromotion] = useState({
    isActive: false,
    format: 'all',
    discountPercent: 10,
    validUntil: '2025-12-31T23:59',
    message: '10% popusta na sve porudžbine!',
    customDisplayText: '',
    applyDiscount: true,
    type: 'discount', // 'discount' or 'gift'
    giftTiers: [],
    tiersEnabled: true // Enable/Disable multi-tier gift system
  });
  const [products, setProducts] = useState([]);
  const [showProductSelector, setShowProductSelector] = useState(false);
  const [currentTierIndex, setCurrentTierIndex] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Promocija | Fotoexpres';
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/logovanje');
      return;
    }
    fetchPromotion();
    fetchProducts();
  }, [navigate]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      if (response.data.success) {
        setProducts(response.data.products.filter(p => p.available));
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchPromotion = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/promotion`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setPromotion(response.data.promotion);
    } catch (error) {
      toast({
        title: "Greška",
        description: "Nije moguće učitati promociju",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(`${API}/admin/promotion`, 
        { promotion },
        { headers: { 'Authorization': `Bearer ${token}` }}
      );
      
      toast({
        title: "Uspešno sačuvano",
        description: "Promocija je ažurirana"
      });
    } catch (error) {
      toast({
        title: "Greška",
        description: "Nije moguće sačuvati promociju",
        variant: "destructive"
      });
    }
  };

  const updatePromotion = (field, value) => {
    setPromotion({
      ...promotion,
      [field]: value
    });
  };

  // Gift Tier Functions
  const addGiftTier = () => {
    const newTier = {
      id: `tier_${Date.now()}`,
      minPhotos: 100,
      gifts: [],
      message: ''
    };
    setPromotion(prev => ({
      ...prev,
      giftTiers: [...prev.giftTiers, newTier]
    }));
  };

  const removeGiftTier = (tierIndex) => {
    setPromotion(prev => ({
      ...prev,
      giftTiers: prev.giftTiers.filter((_, idx) => idx !== tierIndex)
    }));
  };

  const updateGiftTier = (tierIndex, field, value) => {
    setPromotion(prev => ({
      ...prev,
      giftTiers: prev.giftTiers.map((tier, idx) => 
        idx === tierIndex ? { ...tier, [field]: value } : tier
      )
    }));
  };

  const addGiftToTier = (tierIndex, product, variant) => {
    const gift = {
      productId: product.id,
      variantId: variant.id,
      productName: product.name,
      variantName: variant.name,
      price: variant.price
    };

    setPromotion(prev => ({
      ...prev,
      giftTiers: prev.giftTiers.map((tier, idx) => 
        idx === tierIndex ? { ...tier, gifts: [...tier.gifts, gift] } : tier
      )
    }));

    setShowProductSelector(false);
  };

  const removeGiftFromTier = (tierIndex, giftIndex) => {
    setPromotion(prev => ({
      ...prev,
      giftTiers: prev.giftTiers.map((tier, idx) => 
        idx === tierIndex ? { ...tier, gifts: tier.gifts.filter((_, gIdx) => gIdx !== giftIndex) } : tier
      )
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-600">Učitavanje...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <div className="mb-8">
          <Link to="/logovanje/dashboard">
            <Button variant="outline" className="mb-4 gap-2">
              <ArrowLeft size={18} />
              Nazad na Dashboard
            </Button>
          </Link>
          <h1 className="text-4xl font-bold text-gray-900">Pop-up Promocija</h1>
          <p className="text-gray-600 mt-2">Kreirajte i upravljajte pop-up promocijama na sajtu</p>
        </div>

        {/* Info Card */}
        <Card className="p-6 mb-6 bg-blue-50 border-2 border-blue-200">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-semibold text-blue-900 mb-2">Kako funkcioniše?</h3>
              <p className="text-sm text-blue-800">
                Pop-up notifikacija će se prikazati korisnicima kada posete sajt. Možete postaviti popust na određeni format ili sve formate, 
                definisati do kada traje akcija, i napisati poruku koja će se prikazati. Akciju možete aktivirati ili deaktivirati u bilo kom trenutku.
              </p>
            </div>
          </div>
        </Card>

        {/* Promotion Settings */}
        <Card className="p-8">
          <div className="space-y-6">
            {/* Promotion Type Selection */}
            <div>
              <Label className="text-base font-semibold mb-3 block">Tip Promocije</Label>
              <div className="grid grid-cols-2 gap-4">
                <div 
                  onClick={() => updatePromotion('type', 'discount')}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    promotion.type === 'discount' 
                      ? 'border-orange-500 bg-orange-50' 
                      : 'border-gray-300 hover:border-orange-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      promotion.type === 'discount' ? 'border-orange-500' : 'border-gray-300'
                    }`}>
                      {promotion.type === 'discount' && (
                        <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                      )}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">💰 Popust na Cenu</p>
                      <p className="text-xs text-gray-600">Procenat popusta na fotografije</p>
                    </div>
                  </div>
                </div>

                <div 
                  onClick={() => updatePromotion('type', 'gift')}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    promotion.type === 'gift' 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-gray-300 hover:border-green-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      promotion.type === 'gift' ? 'border-green-500' : 'border-gray-300'
                    }`}>
                      {promotion.type === 'gift' && (
                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                      )}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">🎁 Poklon Proizvodi</p>
                      <p className="text-xs text-gray-600">Besplatni proizvodi za broj fotografija</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Active Toggle */}
            <div className="flex items-center justify-between p-6 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border-2 border-purple-300">
              <div className="flex items-center gap-4">
                <Bell className="w-8 h-8 text-purple-600" />
                <div>
                  <Label className="text-lg font-semibold text-gray-900 block mb-1">
                    Status Promocije
                  </Label>
                  <p className="text-sm text-gray-600">
                    {promotion.isActive ? 'Pop-up je trenutno AKTIVAN i prikazuje se korisnicima' : 'Pop-up je trenutno NEAKTIVAN'}
                  </p>
                </div>
              </div>
              <Button
                onClick={() => updatePromotion('isActive', !promotion.isActive)}
                className={`px-8 py-6 text-lg font-semibold ${
                  promotion.isActive 
                    ? 'bg-red-600 hover:bg-red-700' 
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {promotion.isActive ? 'Deaktiviraj' : 'Aktiviraj'}
              </Button>
            </div>

            {/* Format Selection */}
            <div>
              <Label className="text-base font-semibold mb-3 block">1. Izaberite Format</Label>
              <Select 
                value={promotion.format} 
                onValueChange={(value) => updatePromotion('format', value)}
              >
                <SelectTrigger className="w-full text-lg border-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Svi Formati</SelectItem>
                  <SelectItem value="9x13">9x13 cm</SelectItem>
                  <SelectItem value="10x15">10x15 cm</SelectItem>
                  <SelectItem value="13x18">13x18 cm</SelectItem>
                  <SelectItem value="15x21">15x21 cm</SelectItem>
                  <SelectItem value="20x30">20x30 cm</SelectItem>
                  <SelectItem value="30x45">30x45 cm</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Discount Percent */}
            <div>
              <Label className="text-base font-semibold mb-3 block">2. Količina Popusta (%)</Label>
              <div className="flex items-center gap-3">
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={promotion.discountPercent}
                  onChange={(e) => updatePromotion('discountPercent', parseInt(e.target.value) || 0)}
                  className="text-2xl font-bold text-center w-40 border-2"
                />
                <span className="text-2xl font-bold text-orange-600">% popusta</span>
              </div>
            </div>

            {/* Valid Until Date */}
            <div>
              <Label className="text-base font-semibold mb-3 block">3. Akcija Važi Do</Label>
              <div className="flex items-center gap-3">
                <Calendar className="w-6 h-6 text-gray-500" />
                <Input
                  type="datetime-local"
                  value={promotion.validUntil}
                  onChange={(e) => updatePromotion('validUntil', e.target.value)}
                  className="text-lg border-2"
                />
              </div>
            </div>

            {/* Message */}
            <div>
              <Label className="text-base font-semibold mb-3 block">4. Poruka za Akciju</Label>
              <Textarea
                value={promotion.message}
                onChange={(e) => updatePromotion('message', e.target.value)}
                placeholder="Unesite poruku koja će se prikazati korisnicima..."
                rows={3}
                className="text-lg border-2"
                maxLength={150}
              />
              <p className="text-sm text-gray-500 mt-2">
                {promotion.message.length}/150 karaktera
              </p>
            </div>

            {/* Apply Discount Checkbox */}
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="applyDiscount"
                  checked={promotion.applyDiscount}
                  onChange={(e) => updatePromotion('applyDiscount', e.target.checked)}
                  className="w-5 h-5 mt-1"
                />
                <div className="flex-1">
                  <Label htmlFor="applyDiscount" className="text-base font-bold block mb-1">
                    💰 Primeni popust na cenu
                  </Label>
                  <p className="text-sm text-gray-600">
                    Otkačite ovo ako želite samo da prikažete reklamnu poruku <strong>BEZ</strong> popusta na cenu.
                    <br/>
                    (npr. "Album na poklon", "Besplatna dostava", itd.)
                  </p>
                </div>
              </div>
            </div>

            {/* Custom Display Text - only if discount is NOT applied */}
            {!promotion.applyDiscount && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <Label className="text-base font-semibold mb-3 block">
                  🎨 Custom Tekst za Badge (umesto "X% OFF")
                </Label>
                <Input
                  value={promotion.customDisplayText}
                  onChange={(e) => updatePromotion('customDisplayText', e.target.value)}
                  placeholder="Npr: Album na poklon, Besplatna dostava, Specijalna ponuda..."
                  className="text-lg border-2"
                  maxLength={50}
                />
                <p className="text-sm text-gray-500 mt-2">
                  Ovaj tekst će se prikazati na badge-u umesto "10% OFF"
                </p>
              </div>
            )}

            {/* Multi-Tier System Toggle */}
            <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-lg">
              <div className="flex items-start gap-4">
                <input
                  type="checkbox"
                  id="tiersEnabled"
                  checked={promotion.tiersEnabled}
                  onChange={(e) => updatePromotion('tiersEnabled', e.target.checked)}
                  className="w-5 h-5 mt-1 cursor-pointer"
                />
                <div className="flex-1">
                  <Label htmlFor="tiersEnabled" className="text-lg font-bold text-gray-900 block mb-2 cursor-pointer">
                    🎁 Aktiviraj Multi-Tier Gift Sistem
                  </Label>
                  <p className="text-sm text-gray-600 mb-2">
                    Omogućava korisnicima da osvoje besplatne proizvode na osnovu broja fotografija koje naruče.
                  </p>
                  <p className="text-xs text-orange-700 font-semibold bg-orange-100 p-2 rounded">
                    ⚠️ Napomena: Isključite ovu opciju ako koristite format popuste (10% OFF, 20% OFF) da izbegnete da obe promocije budu aktivne istovremeno.
                  </p>
                </div>
              </div>
            </div>

            {/* Gift Tiers - Only show if type is 'gift' AND tiersEnabled */}
            {promotion.type === 'gift' && promotion.tiersEnabled && (
              <div className="p-6 bg-green-50 border-2 border-green-200 rounded-lg">
                <div className="flex justify-between items-center mb-4">
                  <Label className="text-lg font-bold text-gray-900">🎁 Gift Tiers (Poklon Proizvodi)</Label>
                  <Button
                    type="button"
                    onClick={addGiftTier}
                    className="bg-green-600 hover:bg-green-700 gap-2"
                  >
                    <Plus size={18} />
                    Dodaj Tier
                  </Button>
                </div>

                <p className="text-sm text-gray-600 mb-4">
                  Kreirajte nivoe poklona prema broju fotografija. Npr: 100 foto = magnet, 200 foto = šolja + album
                </p>

                {promotion.giftTiers.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    Nema definisanih gift tiers. Kliknite "Dodaj Tier" da započnete.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {promotion.giftTiers.map((tier, tierIndex) => (
                      <Card key={tier.id} className="p-4 bg-white border-2 border-green-300">
                        <div className="flex justify-between items-start mb-4">
                          <h4 className="font-bold text-gray-900 text-lg">Tier {tierIndex + 1}</h4>
                          <Button
                            type="button"
                            size="sm"
                            variant="destructive"
                            onClick={() => removeGiftTier(tierIndex)}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>

                        {/* Min Photos */}
                        <div className="mb-4">
                          <Label className="text-sm font-semibold mb-2 block">
                            Minimalan Broj Fotografija
                          </Label>
                          <Input
                            type="number"
                            min="1"
                            value={tier.minPhotos}
                            onChange={(e) => updateGiftTier(tierIndex, 'minPhotos', parseInt(e.target.value) || 0)}
                            className="w-40"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Korisnik mora naručiti bar ovaj broj fotografija
                          </p>
                        </div>

                        {/* Gift Products */}
                        <div className="mb-4">
                          <Label className="text-sm font-semibold mb-2 block">
                            Poklon Proizvodi
                          </Label>
                          
                          {tier.gifts.length === 0 ? (
                            <div className="text-sm text-gray-500 mb-2 italic">
                              Nema dodanih poklona
                            </div>
                          ) : (
                            <div className="space-y-2 mb-3">
                              {tier.gifts.map((gift, giftIndex) => (
                                <div key={giftIndex} className="flex items-center justify-between bg-green-50 p-3 rounded-lg border border-green-200">
                                  <div className="flex items-center gap-3">
                                    <div className="bg-white p-2 rounded">
                                      🎁
                                    </div>
                                    <div>
                                      <p className="font-semibold text-gray-900">{gift.productName}</p>
                                      <p className="text-sm text-gray-600">{gift.variantName}</p>
                                    </div>
                                  </div>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => removeGiftFromTier(tierIndex, giftIndex)}
                                  >
                                    <Trash2 size={14} className="text-red-500" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}

                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setCurrentTierIndex(tierIndex);
                              setShowProductSelector(true);
                            }}
                            className="border-2 border-dashed border-green-400 hover:bg-green-50"
                          >
                            <Plus size={16} />
                            Dodaj Proizvod
                          </Button>
                        </div>

                        {/* Message */}
                        <div>
                          <Label className="text-sm font-semibold mb-2 block">
                            Poruka za Korisnika
                          </Label>
                          <Textarea
                            value={tier.message}
                            onChange={(e) => updateGiftTier(tierIndex, 'message', e.target.value)}
                            placeholder="Npr: Za 200 fotografija dobijate šolju i album na poklon!"
                            rows={2}
                            className="w-full"
                          />
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Product Selector Modal */}
            {showProductSelector && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <Card className="max-w-4xl w-full mx-4 max-h-[80vh] overflow-auto">
                  <div className="p-6">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-xl font-bold">Odaberi Proizvod</h3>
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => setShowProductSelector(false)}
                      >
                        <X size={24} />
                      </Button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {products.map(product => (
                        <Card key={product.id} className="p-4 hover:shadow-lg transition-shadow">
                          <div className="flex gap-3 mb-3">
                            <img 
                              src={product.imageUrl} 
                              alt={product.name}
                              className="w-20 h-20 object-cover rounded"
                            />
                            <div className="flex-1">
                              <h4 className="font-bold text-gray-900">{product.name}</h4>
                              <p className="text-sm text-gray-600 line-clamp-2">{product.description}</p>
                            </div>
                          </div>

                          <div className="space-y-2">
                            <Label className="text-xs font-semibold text-gray-600">Varijante:</Label>
                            {product.variants.filter(v => v.available).map(variant => (
                              <Button
                                key={variant.id}
                                type="button"
                                onClick={() => addGiftToTier(currentTierIndex, product, variant)}
                                className="w-full justify-between bg-green-600 hover:bg-green-700"
                                size="sm"
                              >
                                <span>{variant.name}</span>
                                <span className="text-xs opacity-75">({variant.price} RSD)</span>
                              </Button>
                            ))}
                          </div>
                        </Card>
                      ))}
                    </div>
                  </div>
                </Card>
              </div>
            )}

            {/* Preview */}
            {promotion.isActive && (
              <div className="mt-8 p-6 bg-gradient-to-r from-orange-100 to-red-100 rounded-lg border-2 border-orange-300">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Bell size={20} />
                  Pregled Notification Trake:
                </h3>
                <div className="bg-gradient-to-r from-orange-600 via-red-600 to-orange-600 text-white rounded-lg shadow-lg overflow-hidden">
                  <div className="px-6 py-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-4 flex-1">
                        <div className="bg-white text-orange-600 px-4 py-2 rounded-full font-bold text-xl">
                          {promotion.applyDiscount 
                            ? `${promotion.discountPercent}% OFF`
                            : (promotion.customDisplayText || 'Custom Tekst')
                          }
                        </div>
                        <div className="flex-1">
                          <p className="text-lg font-bold">
                            {promotion.message || 'Unesite poruku...'}
                          </p>
                          <div className="flex items-center gap-4 text-sm mt-1 opacity-90">
                            <span>📌 {promotion.format === 'all' ? 'Svi formati' : `${promotion.format} cm`}</span>
                            {promotion.validUntil && (
                              <span>
                                📅 Do: {new Date(promotion.validUntil).toLocaleDateString('sr-RS', {
                                  day: 'numeric',
                                  month: 'numeric',
                                  year: 'numeric'
                                })}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <button className="bg-white text-orange-600 px-6 py-2 rounded-full font-bold">
                        Iskoristi Sada!
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Save Button */}
          <div className="flex justify-end mt-8 pt-6 border-t-2">
            <Button
              onClick={handleSave}
              size="lg"
              className="bg-orange-600 hover:bg-orange-700 text-white gap-2 px-8"
            >
              <Save size={20} />
              Sačuvaj Promociju
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AdminPromotion;
